/**
 * Reports a microphone that is ON and hearing NOTHING, once per silent
 * stretch.
 *
 * This is the failure `MIC_OFF` could never catch: it fires when there is no
 * live audio track, and a device muted in the operating system gives the
 * browser a perfectly live track carrying digital silence. The app said the
 * room was being recorded, the encoder wrote an hour of nothing, and the
 * attempt held no record of it — the reviewer found out by trying to listen,
 * weeks later.
 *
 * Deliberately a REPORT and not a block: the same reasoning as the setup
 * step's, which is that a student cannot fix their sound card mid-paper and
 * the exam is worth more than the room audio.
 */
import { useEffect, useRef } from "react";
import { shouldReportSilence } from "./micLevel";
import { useMicLevel } from "./useMicLevel";

export interface SilentMicReportOptions {
  stream: MediaStream | null;
  /** Only while the exam is actually running. */
  active: boolean;
  /** Called once per silent stretch, with how long it has been silent. */
  onSilent: (deadForMs: number) => void;
}

export function useSilentMicReport({
  stream,
  active,
  onSilent,
}: SilentMicReportOptions): void {
  // No meter: nothing on this page draws a bar, and publishing a level on
  // every sample would re-render the whole paper for it.
  const mic = useMicLevel(stream, active, { meter: false });
  const reported = useRef(false);

  const onSilentRef = useRef(onSilent);
  onSilentRef.current = onSilent;

  useEffect(() => {
    if (!active) return;

    // Sound came back — a microphone muted twice is worth reporting twice,
    // and a stream re-acquired mid-exam starts a fresh stretch. Without this
    // the first cough of the hour buys silence for the other fifty-nine
    // minutes.
    if (mic.deadForMs !== null && mic.deadForMs < 1_000) {
      reported.current = false;
      return;
    }

    if (
      !shouldReportSilence({
        deadForMs: mic.deadForMs,
        listening: mic.listening,
        alreadyReported: reported.current,
      })
    ) {
      return;
    }
    reported.current = true;
    onSilentRef.current(Math.round(mic.deadForMs ?? 0));
  }, [active, mic.deadForMs, mic.listening]);
}
